import { useState } from "react";
import { Layers, Loader2, WifiOff, ChevronDown } from "lucide-react";
import { fmtUsd } from "@/lib/api";
import { Hint } from "@/components/Hint";

const fmtSize = (s) => (s >= 1000 ? s.toFixed(0) : s >= 1 ? s.toFixed(3) : s >= 0.00001 ? s.toFixed(5) : "<0.00001");
const digitsFor = (p) => (p < 1 ? 6 : p < 100 ? 4 : 2);

const Row = ({ level, side, maxCum }) => {
  const pct = maxCum ? (level.cum / maxCum) * 100 : 0;
  const isBid = side === "bid";
  return (
    <div className="relative grid grid-cols-3 text-[11px] font-mono-ticker px-2 py-[3px] leading-tight" data-testid={`ob-${side}-row`}>
      <div
        className="absolute inset-y-0 pointer-events-none"
        style={{ [isBid ? "right" : "left"]: 0, width: `${pct}%`, background: isBid ? "rgba(16,185,129,0.14)" : "rgba(239,68,68,0.14)" }}
      />
      <span className={isBid ? "text-emerald-300" : "text-red-300"}>{fmtUsd(level.p, { digits: digitsFor(level.p) })}</span>
      <span className="text-right text-slate-200">{fmtSize(level.s)}</span>
      <span className="text-right text-slate-500">{fmtSize(level.cum)}</span>
    </div>
  );
};

export const OrderBook = ({ book, symbol }) => {
  const { status, bids: allBids, asks: allAsks, mid, spread, maxCum } = book;
  const [expanded, setExpanded] = useState(() => typeof window === "undefined" || window.matchMedia("(min-width: 768px)").matches);
  const bids = expanded ? allBids : allBids.slice(0, 6), asks = expanded ? allAsks : allAsks.slice(0, 6);
  const hidden = (allBids.length - bids.length) + (allAsks.length - asks.length);
  const bidDepth = allBids.reduce((a, l) => a + l.s, 0);
  const askDepth = allAsks.reduce((a, l) => a + l.s, 0);
  const total = bidDepth + askDepth || 1;
  const buyPct = (bidDepth / total) * 100;
  return (
    <div className="apx-card p-3 h-full flex flex-col" data-testid="order-book">
      <div className="flex items-center gap-2 mb-2">
        <Hint title="Order book depth" text="Live level-2 order book from the EV-IDEX market feed: resting sell orders (asks) above the mid price, buy orders (bids) below. Bars show cumulative size — thicker walls mean more supply or demand at that level." asChild>
          <span className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400 inline-flex items-center gap-1 cursor-help"><Layers className="w-3 h-3 text-cyan-300" /> Depth · {symbol?.toUpperCase()}/USD</span>
        </Hint>
        <span className="ml-auto text-[10px] font-mono-ticker text-slate-500">
          {status === "live" ? `spread ${spread != null ? fmtUsd(spread, { digits: spread < 0.01 ? 6 : 2 }) : "—"}` : status === "connecting" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <WifiOff className="w-3 h-3 inline" />}
        </span>
      </div>
      {status !== "live" ? (
        <div className="flex-1 flex items-center justify-center text-xs text-slate-500 text-center px-2" data-testid={`ob-status-${status}`}>
          {status === "unavailable" ? "No order book available for this asset." : "Loading order book…"}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 text-[9px] font-mono-ticker uppercase tracking-widest text-slate-500 px-2 pb-1 border-b border-slate-800">
            <span>Price</span><span className="text-right">Size</span><span className="text-right">Total</span>
          </div>
          <div className="flex flex-col-reverse" data-testid="ob-asks">
            {asks.map((l) => <Row key={l.p} level={l} side="ask" maxCum={maxCum} />)}
          </div>
          <div className="my-1 py-1.5 text-center font-mono-ticker text-sm font-semibold text-slate-100 border-y border-slate-800" data-testid="ob-mid">
            {mid != null ? fmtUsd(mid, { digits: digitsFor(mid) }) : "—"}
          </div>
          <div data-testid="ob-bids">
            {bids.map((l) => <Row key={l.p} level={l} side="bid" maxCum={maxCum} />)}
          </div>
          {(allBids.length > 6 || allAsks.length > 6) && (
            <button data-testid="ob-toggle-levels" onClick={() => setExpanded((v) => !v)} className="md:hidden mt-1 w-full min-h-[40px] text-[11px] font-mono-ticker text-cyan-300 inline-flex items-center justify-center gap-1">
              <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} /> {expanded ? "Show fewer levels" : `Show ${hidden} more levels`}
            </button>
          )}
          <Hint title="Buy vs sell pressure" text="Share of resting size on the bid side vs the ask side within the visible levels." className="block mt-2 cursor-help">
            <div className="h-1.5 rounded-full overflow-hidden flex bg-slate-900" data-testid="ob-pressure">
              <div style={{ width: `${buyPct}%`, background: "#10B981" }} />
              <div style={{ width: `${100 - buyPct}%`, background: "#EF4444" }} />
            </div>
            <div className="flex justify-between text-[9px] font-mono-ticker mt-1 text-slate-500">
              <span className="text-emerald-400">{buyPct.toFixed(0)}% bids</span><span className="text-red-400">{(100 - buyPct).toFixed(0)}% asks</span>
            </div>
          </Hint>
        </>
      )}
    </div>
  );
};
